import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Breadcrumbs, Button, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Grid, IconButton, Link, Paper, Snackbar, Stack, TextField, Typography,
} from '@mui/material';
import {
  ChatBubbleOutline, EventAvailable, NavigateBefore, NavigateNext, Public, Share,
} from '@mui/icons-material';
import Seo from '../components/Seo';
import VerificationBadgePanel from '../components/listings/VerificationBadgePanel';
import VerifiedChip from '../components/listings/VerifiedChip';
import PhotoMosaic from '../components/listings/PhotoMosaic';
import KeyFacts from '../components/listings/KeyFacts';
import CessionExplainer from '../components/listings/CessionExplainer';
import DetailsTable from '../components/listings/DetailsTable';
import FeatureChips from '../components/listings/FeatureChips';
import SuburbMap from '../components/listings/SuburbMap';
import SimilarHomes from '../components/listings/SimilarHomes';
import SaveButton from '../components/listings/SaveButton';
import SignInToSaveDialog from '../components/listings/SignInToSaveDialog';
import ViewingDialog from '../components/listings/ViewingDialog';
import { forgetPendingSave, readPendingSave } from '../components/listings/pendingSave';
import { fmtPlace, fmtPrice } from '../components/listings/format';
import publicListingsService from '../services/publicListingsService';
import { shareProfileLink } from '../utils/shareLink';
import { formatHarareDateShort } from '../utils/time';
import { useAuth } from '../contexts/AuthContext';
import { border, brand, status as statusColors, text } from '../theme/colors';

/** Meta descriptions are truncated by search engines around here; cut on a word so it reads cleanly. */
const SEO_DESCRIPTION_MAX = 150;
const trimToWord = (s, max) => {
  const t = s.trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).replace(/[.,;:]$/, '')}…`;
};

/**
 * The EAC register records an agency as "compliant". That is the only standing
 * that earns the badge — anything else (lapsed, unknown, absent) simply shows
 * the agency name with no claim attached to it.
 */
const isEacRegistered = (s) => String(s || '').toLowerCase() === 'compliant';

/** The server's own words for a refusal, when it gave any; otherwise a plain fallback. */
const serverMessage = (e, fallback) => {
  const d = e?.response?.data;
  return (typeof d?.error === 'string' && d.error) || (typeof d?.message === 'string' && d.message) || fallback;
};

/** Whole days since a payload date — null when it carries none, or an unusable one. */
const daysSince = (value) => {
  if (!value) return null;
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(Math.floor((Date.now() - then) / 86400000), 0);
};

/** "Listed today" / "Listed 6 days ago" — null when the payload carries no date. */
const listedAgo = (days) => {
  if (days == null) return null;
  if (days === 0) return 'Listed today';
  if (days === 1) return 'Listed yesterday';
  return `Listed ${days} days ago`;
};

/**
 * The browse card says only "New" — there is no room for more. Here there is,
 * and "New · 3 days" answers the question the badge provokes: how new?
 */
const newLabel = (days) => {
  if (days == null) return 'New';
  if (days === 0) return 'New · today';
  return `New · ${days} ${days === 1 ? 'day' : 'days'}`;
};

/** A section heading plus its body, spaced the same way down the whole page. */
function Section({ title, children, sx = {} }) {
  return (
    <Box sx={{ mt: 4, ...sx }}>
      {title && (
        <Typography variant="h6" sx={{ fontWeight: 800, color: text.primary, mb: 1.5 }}>{title}</Typography>
      )}
      {children}
    </Box>
  );
}

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [gone, setGone] = useState(false);

  const [lightbox, setLightbox] = useState(null);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [viewingOpen, setViewingOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [enquiryError, setEnquiryError] = useState('');
  const [viewingError, setViewingError] = useState('');
  const [toast, setToast] = useState('');
  // Optimistic: the payload's `saved` flag is the baseline and this holds the
  // buyer's unconfirmed intent on top of it.
  const [savedOverride, setSavedOverride] = useState(null);

  useEffect(() => {
    let live = true;
    const load = async () => {
      // Everything below is about one listing id — carrying any of it across a
      // navigation would show the previous property's photos or draft message.
      setListing(null); setLightbox(null); setEnquiryOpen(false); setViewingOpen(false); setMessage('');
      setSavedOverride(null); setToast(''); setEnquiryError(''); setViewingError('');
      setLoading(true); setLoadError(''); setGone(false);
      try {
        const res = await publicListingsService.get(id);
        if (live) setListing(res.data);
      } catch (e) {
        if (!live) return;
        if (e?.response?.status === 404) setGone(true);
        else setLoadError('We could not load this property just now. Please try again in a moment.');
      } finally {
        if (live) setLoading(false);
      }
    };
    load();
    return () => { live = false; };
  }, [id]);

  // Memoised because the lightbox stepper closes over it: a fresh array on every
  // render would rebind the keydown listener on every render too.
  const photos = useMemo(
    () => (Array.isArray(listing?.photos) ? listing.photos.filter((p) => p?.url) : []),
    [listing],
  );

  /** Arrow keys are how a buyer walks a photo set; the lightbox wraps at both ends. */
  const stepPhoto = useCallback((delta) => {
    setLightbox((cur) => {
      if (!cur || photos.length === 0) return cur;
      const next = (cur.index + delta + photos.length) % photos.length;
      return { index: next, url: photos[next].url };
    });
  }, [photos]);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') stepPhoto(1);
      if (e.key === 'ArrowLeft') stepPhoto(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, stepPhoto]);

  const openEnquiry = useCallback(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location, notice: 'Sign in to enquire about this property.' } });
      return;
    }
    setEnquiryError(''); setEnquiryOpen(true);
  }, [isAuthenticated, navigate, location]);

  const openViewing = useCallback(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location, notice: 'Sign in to request a viewing.' } });
      return;
    }
    setViewingError(''); setViewingOpen(true);
  }, [isAuthenticated, navigate, location]);

  /** Both buttons end in the same place: the thread with the agent. */
  const send = async (payload, { onError }) => {
    setSending(true);
    try {
      const res = await publicListingsService.enquire(id, payload);
      setEnquiryOpen(false); setViewingOpen(false);
      // The enquiry landed either way; without an id, Messages still shows the thread.
      const conversationId = res?.data?.conversationId;
      navigate(conversationId ? `/messages?conversation=${conversationId}` : '/messages');
    } catch (e) {
      onError(serverMessage(e, 'We could not send your message just now. Please try again in a moment.'));
    } finally {
      setSending(false);
    }
  };

  const sendEnquiry = () => {
    setEnquiryError('');
    return send({ message: message.trim(), kind: 'enquiry' }, { onError: setEnquiryError });
  };

  const sendViewing = ({ preferredDates, message: note }) => {
    setViewingError('');
    return send({ kind: 'viewing', preferredDates, message: note }, { onError: setViewingError });
  };

  const toggleSave = useCallback(async (nextSaved) => {
    setSavedOverride(nextSaved);
    try {
      if (nextSaved) await publicListingsService.save(id);
      else await publicListingsService.unsave(id);
      setToast(nextSaved ? 'Saved to your homes' : 'Removed from your homes');
    } catch (e) {
      setSavedOverride(!nextSaved);
      setToast(e?.response?.data?.error || 'We could not update your saved homes. Please try again.');
    }
  }, [id]);

  // Logged out: ask first (log in or create an account) — see SignInToSaveDialog.
  const [signInFor, setSignInFor] = useState(null);
  const requireLogin = useCallback(() => setSignInFor(id), [id]);

  // Finish a save the buyer asked for before signing in.
  useEffect(() => {
    if (!isAuthenticated || !listing) return;
    const pending = location.state?.saveListingId || readPendingSave();
    if (pending !== id || savedOverride != null || listing.saved) return;
    forgetPendingSave();
    if (location.state?.saveListingId) navigate(location.pathname + location.search, { replace: true, state: null });
    toggleSave(true);
  }, [isAuthenticated, listing, location, navigate, id, savedOverride, toggleSave]);

  // On a phone this opens the native share sheet (WhatsApp is how these links
  // actually travel); the snackbar is only honest when it really hit the clipboard.
  const share = async () => {
    const how = await shareProfileLink({ path: `/share/properties/${id}`, title: listing?.title || 'Property on Eqaya' });
    if (how === 'copied') setToast('Link copied');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: brand.primary }} aria-label="Loading property" />
      </Container>
    );
  }

  if (gone || (!listing && !loadError)) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Seo title="Property not available" canonicalPath={`/properties/${id}`} />
        <Alert severity="info" sx={{ mb: 2 }}>This listing is no longer available.</Alert>
        <Link component={RouterLink} to="/properties">Back to verified properties</Link>
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Seo title="Property" canonicalPath={`/properties/${id}`} />
        <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
        <Link component={RouterLink} to="/properties">Back to verified properties</Link>
      </Container>
    );
  }

  const d = listing;
  const place = fmtPlace(d.location);
  const isHouse = d.listing_type === 'house';
  const agency = d.realtor?.agency || null;
  const sold = !!d.sold_at;
  const isOwnListing = isAuthenticated && user?.id && d.realtor?.id === user.id;
  const saved = savedOverride == null ? !!d.saved : savedOverride;
  const soldOn = formatHarareDateShort(d.sold_at);
  const liveDays = daysSince(d.published_at);
  const listed = listedAgo(liveDays);
  const seoDescription = (d.description && trimToWord(d.description, SEO_DESCRIPTION_MAX))
    || `${isHouse ? 'House' : 'Stand'}${place ? ` in ${place}` : ''}, independently verified by Eqaya before listing.`;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Seo title={`${d.title} — Eqaya Verified`} description={seoDescription} canonicalPath={`/properties/${id}`} />

      <Breadcrumbs sx={{ mb: 2 }} aria-label="Breadcrumb">
        <Link component={RouterLink} to="/properties" variant="body2" underline="hover">Verified properties</Link>
        {d.location?.city && (
          <Link
            component={RouterLink}
            to={`/properties?city=${encodeURIComponent(d.location.city)}`}
            variant="body2"
            underline="hover"
          >
            {d.location.city}
          </Link>
        )}
        <Typography variant="body2" sx={{ color: text.tertiary }}>{d.title}</Typography>
      </Breadcrumbs>

      <PhotoMosaic photos={photos} title={d.title} onOpen={(i) => setLightbox({ index: i, url: photos[i].url })} />

      {sold && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {soldOn ? `Sold on ${soldOn}` : 'Sold'}
        </Alert>
      )}

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <VerifiedChip />
            <Chip size="small" label={isHouse ? 'House' : 'Stand'} variant="outlined" />
            {d.tenure_type === 'cession' && <Chip size="small" label="Cession" sx={{ bgcolor: '#9a3412', color: '#fff', fontWeight: 700 }} />}
            {sold && <Chip size="small" label="Sold" sx={{ bgcolor: text.primary, color: '#fff', fontWeight: 700 }} />}
            {!sold && d.is_new && (
              <Chip size="small" label={newLabel(liveDays)} sx={{ bgcolor: brand.secondary, color: '#fff', fontWeight: 700 }} />
            )}
          </Stack>

          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: sold ? text.tertiary : text.primary,
              textDecoration: sold ? 'line-through' : 'none',
            }}
          >
            {fmtPrice(d.price_usd)}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: text.primary, fontWeight: 600, mt: 0.25 }}>{d.title}</Typography>
          {place && <Typography variant="body2" sx={{ color: text.secondary, mb: 1.5 }}>{place}</Typography>}

          <KeyFacts listing={d} />

          {/* A cession has no title deed: say so plainly, before the buyer reads further. */}
          {d.tenure_type === 'cession' && (
            <Box sx={{ mt: 3 }}><CessionExplainer facts={d.cession} /></Box>
          )}

          {d.description && (
            <Section title={isHouse ? 'About this home' : 'About this stand'}>
              <Typography variant="body1" sx={{ color: text.primary, whiteSpace: 'pre-line' }}>{d.description}</Typography>
            </Section>
          )}

          <Section>
            <VerificationBadgePanel
              panel={d.verification_panel}
              deedValidatedSi76={d.tenure_type !== 'cession' && d.deed_validated_si76}
              conveyancerFirm={d.conveyancer?.firm || null}
            />
          </Section>

          <Section title="Property details">
            <DetailsTable listing={d} />
          </Section>

          {Array.isArray(d.features) && d.features.length > 0 && (
            <Section title="Features">
              <FeatureChips features={d.features} />
            </Section>
          )}

          {d.location?.pin && (
            <Section title="Location">
              <SuburbMap pin={d.location.pin} />
            </Section>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 24 } }}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Typography variant="overline" sx={{ color: text.tertiary }}>Listed by</Typography>
              {d.realtor?.display_name && (
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: text.primary }}>{d.realtor.display_name}</Typography>
              )}
              {agency?.name && (
                <>
                  <Typography variant="body2" sx={{ color: text.secondary, mt: 0.25 }}>{agency.name}</Typography>
                  {isEacRegistered(agency.status) && (
                    <Chip
                      size="small"
                      component={RouterLink}
                      clickable
                      to={`/realtors?q=${encodeURIComponent(agency.name)}`}
                      label="Registered with EAC ✓"
                      sx={{ mt: 1, color: statusColors.success, bgcolor: statusColors.successBg, fontWeight: 600 }}
                    />
                  )}
                </>
              )}

              <Divider sx={{ my: 2 }} />

              <Stack spacing={1.5}>
                {!isOwnListing && !sold && (
                  <>
                    <Button
                      fullWidth variant="contained" startIcon={<ChatBubbleOutline />} onClick={openEnquiry}
                      sx={{ background: brand.gradient, '&:hover': { background: brand.gradientHover } }}
                    >
                      Enquire about this property
                    </Button>
                    <Button
                      fullWidth variant="outlined" startIcon={<EventAvailable />} onClick={openViewing}
                      sx={{ borderColor: brand.primary, color: brand.primary }}
                    >
                      Request a viewing
                    </Button>
                  </>
                )}
                <Button fullWidth variant="outlined" startIcon={<Share />} onClick={share}>Share this listing</Button>
              </Stack>
              <Typography variant="caption" sx={{ color: text.tertiary, display: 'block', mt: 2 }}>
                Your enquiry opens a message thread with the agent on Eqaya, so there is a record of what was agreed.
              </Typography>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, mt: 2, borderColor: border.light }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Public sx={{ color: brand.primary, fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: text.primary }}>Buying from abroad?</Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: text.secondary }}>
                Eqaya checks the property, the paperwork and the agent before you send anyone money, and everything
                you agree stays in writing.
              </Typography>
              <Link component={RouterLink} to="/buying-property" variant="body2" sx={{ display: 'inline-block', mt: 1, fontWeight: 600 }}>
                What we check, and how buying works →
              </Link>
            </Paper>

            {!sold && !isOwnListing && (
              <Paper variant="outlined" sx={{ p: 2.5, mt: 2, borderColor: border.light }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: text.primary }}>Price watch</Typography>
                <Typography variant="body2" sx={{ color: text.secondary, mb: 1.5 }}>
                  {listed ? `${listed} at ${fmtPrice(d.price_usd)}.` : `Asking ${fmtPrice(d.price_usd)}.`}
                  {' '}
                  Save it and it will be waiting in your account.
                </Typography>
                <SaveButton
                  withLabel
                  saved={saved}
                  disabled={!isAuthenticated}
                  onToggle={() => toggleSave(!saved)}
                  onRequireLogin={requireLogin}
                />
              </Paper>
            )}
          </Box>
        </Grid>
      </Grid>

      <SimilarHomes id={id} />

      <Dialog open={Boolean(lightbox)} onClose={() => setLightbox(null)} maxWidth="lg" fullWidth aria-labelledby="listing-photo-title">
        <DialogTitle id="listing-photo-title">{d.title} — photo {(lightbox?.index ?? 0) + 1} of {photos.length}</DialogTitle>
        <DialogContent>
          {lightbox && (
            <Box sx={{ position: 'relative' }}>
              <Box component="img" src={lightbox.url} alt={`${d.title} — photo ${lightbox.index + 1}, enlarged`}
                sx={{ width: '100%', height: 'auto', display: 'block' }} />
              {photos.length > 1 && (
                <>
                  <IconButton
                    aria-label="Previous photo"
                    onClick={() => stepPhoto(-1)}
                    sx={{ position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: '#fff' } }}
                  >
                    <NavigateBefore />
                  </IconButton>
                  <IconButton
                    aria-label="Next photo"
                    onClick={() => stepPhoto(1)}
                    sx={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: '#fff' } }}
                  >
                    <NavigateNext />
                  </IconButton>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setLightbox(null)}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={enquiryOpen} onClose={() => (sending ? null : setEnquiryOpen(false))} maxWidth="sm" fullWidth aria-labelledby="listing-enquiry-title">
        <DialogTitle id="listing-enquiry-title">Enquire about this property</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: text.secondary, mb: 2 }}>
            We will open a message thread with the agent and send them the details of this listing. Add a note if you
            would like — otherwise we will simply ask whether it is still available.
          </Typography>
          {enquiryError && <Alert severity="warning" sx={{ mb: 2 }}>{enquiryError}</Alert>}
          <TextField
            fullWidth multiline minRows={3} label="Your message (optional)"
            inputProps={{ maxLength: 2000 }}
            value={message} onChange={(e) => setMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnquiryOpen(false)} disabled={sending}>Cancel</Button>
          <Button variant="contained" onClick={sendEnquiry} disabled={sending}
            sx={{ background: brand.gradient, '&:hover': { background: brand.gradientHover } }}>
            {sending ? 'Sending…' : 'Send enquiry'}
          </Button>
        </DialogActions>
      </Dialog>

      <ViewingDialog
        open={viewingOpen}
        sending={sending}
        error={viewingError}
        onClose={() => setViewingOpen(false)}
        onSubmit={sendViewing}
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast('')} message={toast} />
      <SignInToSaveDialog listingId={signInFor} onClose={() => setSignInFor(null)} />
    </Container>
  );
}
